import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, RefreshCw, BookOpenCheck, Trophy, GraduationCap, ShieldAlert, Download } from "lucide-react";
import { celebrate } from "@/components/Celebrate";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const MODELS = [
  { key: "openai/gpt-5.2", label: "GPT-5.2 (OpenAI)" },
  { key: "anthropic/claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
  { key: "gemini/gemini-3-flash-preview", label: "Gemini 3 Flash" },
];

export default function Roadmap() {
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [career, setCareer] = useState("");
  const [model, setModel] = useState(MODELS[0].key);
  const [careersList, setCareersList] = useState([]);
  const pdfRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get("/roadmap/"),
      api.get("/careers/"),
    ]).then(([r, c]) => {
      if (r.data && r.data.milestones) setRoadmap(r.data);
      setCareersList(c.data);
    }).finally(() => setLoading(false));
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/roadmap/generate", { career_target: career || undefined, model });
      setRoadmap(res.data);
      toast.success("Your roadmap is ready!");
      celebrate("Roadmap ready!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const downloadPdf = async () => {
    if (!pdfRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(pdfRef.current, {
        backgroundColor: "#0B1120", scale: 2, useCORS: true,
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW;
      const imgH = (canvas.height * imgW) / canvas.width;
      let heightLeft = imgH;
      let position = 0;
      pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH, undefined, "FAST");
      heightLeft -= pageH;
      while (heightLeft > 0) {
        position -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH, undefined, "FAST");
        heightLeft -= pageH;
      }
      const title = (roadmap?.career_target || "career-roadmap").replace(/\s+/g, "-").toLowerCase();
      pdf.save(`brimi-${title}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed");
    } finally { setDownloading(false); }
  };

  if (loading) return <AppShell><div className="p-10 text-slate-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading roadmap…</div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-6 py-10" data-testid="roadmap-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300">AI Roadmap</p>
            <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Your career plan.</h1>
            <p className="text-slate-400 mt-2 max-w-xl">Powered by AI. Personalized to your grade, marks and interests.</p>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-8 brimi-card p-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Target career</label>
              <select data-testid="roadmap-career-select" value={career} onChange={(e) => setCareer(e.target.value)} className="mt-2 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-slate-900 outline-none focus:ring-2 focus:ring-blue-100">
                <option value="">Auto (based on quiz)</option>
                {careersList.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">AI Model</label>
              <select data-testid="roadmap-model-select" value={model} onChange={(e) => setModel(e.target.value)} className="mt-2 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-slate-900 outline-none focus:ring-2 focus:ring-blue-100">
                {MODELS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button data-testid="roadmap-generate-button" onClick={generate} disabled={generating} className="brimi-pill accent-coral flex items-center justify-center gap-2 w-full disabled:opacity-60">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> {roadmap ? "Regenerate" : "Generate roadmap"}</>}
              </button>
            </div>
          </div>
        </div>

        {roadmap ? (
          <div className="mt-8 space-y-6" data-testid="roadmap-content" ref={pdfRef}>
            <div className="flex justify-end -mb-2">
              <button data-testid="roadmap-download-pdf" onClick={downloadPdf} disabled={downloading}
                className="brimi-pill accent-yellow flex items-center gap-2 text-sm disabled:opacity-60">
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Download PDF
              </button>
            </div>
            <div className="brimi-card p-8">
              <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-500">Target</p>
              <h2 className="font-display text-3xl text-slate-900 mt-1">{roadmap.career_target}</h2>
              <p className="text-slate-700 mt-3">{roadmap.summary}</p>
              {roadmap.why_this_fits && (
                <div className="mt-4 p-4 rounded-xl bg-yellow-50 border border-yellow-200">
                  <p className="text-xs font-bold text-yellow-800 uppercase tracking-[0.15em]">Why this fits you</p>
                  <p className="text-slate-800 mt-1">{roadmap.why_this_fits}</p>
                </div>
              )}
              <div className="grid sm:grid-cols-3 gap-4 mt-5 text-sm">
                <MetaBlock icon={BookOpenCheck} label="Recommended streams" values={roadmap.recommended_streams} />
                <MetaBlock icon={Trophy} label="Top entrance exams" values={roadmap.top_entrance_exams} />
                <MetaBlock icon={ShieldAlert} label="Backup options" values={roadmap.backup_options} />
              </div>
            </div>

            {/* Timeline */}
            <div className="brimi-card p-8">
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Milestones</p>
              <h3 className="font-display text-2xl text-slate-900 mt-1">Your step-by-step plan</h3>
              <div className="mt-6 space-y-6">
                {(roadmap.milestones || []).map((m, i) => (
                  <div key={i} className="timeline-node pl-14 relative" data-testid={`milestone-${i}`}>
                    <div className="absolute left-2 top-1 w-9 h-9 rounded-full flex items-center justify-center font-display text-sm text-slate-900" style={{ background: milestoneColor(i) }}>
                      {i + 1}
                    </div>
                    <div className="flex flex-wrap items-baseline gap-2">
                      <p className="font-display text-xl text-slate-900">{m.title}</p>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">{m.grade}</span>
                      {m.timeframe && <span className="text-xs text-slate-500">· {m.timeframe}</span>}
                    </div>
                    {m.tasks?.length > 0 && (
                      <ul className="mt-3 space-y-1 text-sm text-slate-700 list-disc list-inside">
                        {m.tasks.map((t, j) => <li key={j}>{t}</li>)}
                      </ul>
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(m.exams || []).map((e) => <span key={e} className="text-[11px] px-2 py-1 rounded-full bg-blue-50 text-blue-700 font-bold border border-blue-100">📝 {e}</span>)}
                      {(m.skills || []).map((s) => <span key={s} className="text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-bold border border-emerald-100">✓ {s}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Colleges */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="brimi-card p-6">
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-blue-600" />
                  <p className="font-display text-xl text-slate-900">India college paths</p>
                </div>
                <ul className="mt-3 space-y-1 text-slate-700 list-disc list-inside text-sm">
                  {(roadmap.college_paths_india || []).map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
              <div className="brimi-card p-6">
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-coral-500" style={{ color: "#FB7185" }} />
                  <p className="font-display text-xl text-slate-900">Global college paths</p>
                </div>
                <ul className="mt-3 space-y-1 text-slate-700 list-disc list-inside text-sm">
                  {(roadmap.college_paths_global || []).map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            </div>

            <p className="text-xs text-slate-500 text-center pb-4">Generated with {roadmap.model_used || model}. Always verify exam dates & cutoffs from official sources.</p>
          </div>
        ) : (
          <div className="mt-12 brimi-card p-10 text-center">
            <Sparkles className="w-10 h-10 mx-auto text-yellow-400" />
            <p className="font-display text-2xl text-slate-900 mt-4">No roadmap yet</p>
            <p className="text-slate-500 mt-1">Pick a target career (or leave auto) and hit generate to get your personalized plan.</p>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function MetaBlock({ icon: Icon, label, values }) {
  return (
    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
      <p className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {label}</p>
      <p className="text-sm text-slate-800 mt-1">{(values || []).join(", ") || "—"}</p>
    </div>
  );
}

function milestoneColor(i) {
  const colors = ["#FBBF24", "#FB7185", "#34D399", "#3B82F6", "#8B5CF6", "#F97316", "#EC4899", "#14B8A6"];
  return colors[i % colors.length];
}

